/*
  The header's view of the product catalogue.

  Derived from `@/content/products`, which is also what the pages are built
  from, so the menu cannot list a product with no page or miss one that has
  a page. This used to be a hand-kept second copy with a test pinning the
  two lists to the same slugs; deriving it removes the failure instead of
  detecting it.
*/
import { productGroups, products } from '@/content/products'

export interface MenuProduct {
  slug: string
  name: string
  blurb: string
}

export interface MenuGroup {
  title: string
  items: MenuProduct[]
}

export const PRODUCT_MENU: MenuGroup[] = productGroups.map((group) => ({
  title: group.title,
  items: products
    .filter((product) => product.group === group.id)
    .map(({ slug, name, blurb }) => ({ slug, name, blurb })),
}))

/* The featured panel on the left of the menu. One thing, and it is the one
   with the largest price on it. */
export const PRODUCT_MENU_FEATURE = {
  href: '/audit',
  /*
    A crop made for this slot rather than the full-bleed ground it came from.

    It pointed at grounds-1/ground-swirl-07.webp, which is 1536x1024 and 219K
    because it is a background image, shown here in a strip 132px tall. object-fit
    threw away most of what was downloaded. Same picture, same visible crop,
    800x320 and 53K.
  */
  photo: '/design-system/mega-menu/products-visibility.webp',
  title: 'The SuperSchema Audit',
  blurb: 'A done-for-you diagnostic: what AI understands about you today, where the evidence breaks, and what to fix first.',
  cta: 'See what is in it',
}

/* ---------------- The pricing menu ---------------- */

/*
  Pricing forks before it lands: a brand buying for its own site, an agency
  buying to resell, and a business handing the whole job to us are three
  different pages, and asking somebody to work out which one they want after
  they arrive is a worse question than asking it in the nav.

  Three cards in the same shape the products menu's featured card uses, so
  the two menus read as one system rather than two. Managed left the menu
  for a day (2026-09-14) and is back on Kevin's call (2026-09-16), as the
  third card, beside the footer link it kept throughout.

  The cards are labelled by audience, "Businesses", "Agencies" and "Managed
  by Us", and the words are set into the photographs. They were briefly
  "Do it yourself" / "Your agency does it for you" (2026-09-15); Kevin asked
  for the original pictures back (2026-09-16).

  `alt` is real text here and empty on the products card, and the
  difference is not an oversight. These photographs carry the category set
  into them, and since the titles below do not repeat it, the category
  exists nowhere else in the card. An empty alt would leave a screen reader
  unable to tell them apart. The products card's picture is a ground with
  nothing written on it, so it stays decorative.

  `short` is the category as a phrase, and it exists because the phone menu
  has no room for a mega menu and renders these as rows. It used to build
  those rows from the title, which produced "Pricing improve your company's
  visibility in ai search". The row now reads "Pricing: Businesses". It is
  its own field rather than the `alt`, because what a picture needs
  described and what a nav row needs labelled are two questions that only
  happen to have the same answer today.
*/
export const PRICING_MENU = [
  {
    href: '/pricing',
    photo: '/design-system/mega-menu/pricing-businesses.webp',
    alt: 'Businesses',
    short: 'Businesses',
    title: 'Improve your company’s visibility in AI search',
    blurb: 'Plans for a company looking after its own site: what AI can read about you, fixed and watched.',
    cta: 'See plans',
  },
  {
    href: '/agency',
    photo: '/design-system/mega-menu/pricing-agencies.webp',
    alt: 'Agencies',
    short: 'Agencies',
    title: 'Manage AI visibility across client websites',
    blurb: 'Managed AI visibility you sell under your own brand, with the partner economics published on the page.',
    cta: 'See partner economics',
  },
  {
    href: '/managed',
    photo: '/design-system/mega-menu/pricing-managed.webp',
    alt: 'Managed by Us',
    short: 'Managed by us',
    title: 'Let our team run your AI visibility',
    /*
      The blurb has to let a reader recognise themselves, which the first
      one did not: it ended "the audit, the build, the deploy, and the
      watching", four nominalised verbs that name our internal stages and
      tell a buyer nothing about whether this card is for them.

      What decides it is whether they want to do the work. So the second
      sentence is that question, in the words somebody would use about their
      own team.
    */
    blurb: 'Done for you by the people who built the product. For businesses that would rather hand this over than take it on.',
    cta: 'Let’s work together',
  },
]
